// Polityka prywatności / Privacy Policy
//
// Public page (no auth) so Google OAuth's verification reviewers and
// other third parties can fetch it. Linked from the OAuth consent
// screen in Google Cloud Console.
//
// Polish content is the canonical version (BG Polska's audience).
// English summary at the bottom for non-Polish reviewers.
//
// Trade-off: this version intentionally omits the legal-entity
// address + registration numbers (NIP, KRS) that a fully GDPR-compliant
// controller block would carry. Acceptable for v1 launch; revisit if
// you need a stricter posture (e.g. if you ever take payments or
// process sensitive data).

export function PrivacyPolicyPage() {
  return (
    <div
      data-testid="privacy-policy-page"
      className="mx-auto max-w-3xl px-4 py-8 leading-relaxed text-foreground"
    >
      <h1 className="mb-6 text-3xl font-bold">Polityka prywatności</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Ostatnia aktualizacja: 22 maja 2026 r.
      </p>

      <Section title="1. Administrator danych">
        <p>
          Administratorem Twoich danych osobowych jest{' '}
          <strong>Backgammon Polska</strong> (dalej „
          <strong>Administrator</strong>" lub „<strong>my</strong>").
        </p>
        <p>
          W sprawach związanych z przetwarzaniem danych osobowych możesz
          skontaktować się z nami pod adresem:{' '}
          <a
            className="text-primary underline"
            href="mailto:admin@backgammon.pl"
          >
            admin@backgammon.pl
          </a>
          .
        </p>
      </Section>

      <Section title="2. Operator techniczny">
        <p>
          Platforma turniejowa, z której korzysta Administrator, jest
          dostarczana przez operatora technicznego <strong>Swiss Magic</strong>.
          Operator techniczny działa jako podmiot przetwarzający dane w imieniu
          Administratora (art. 28 RODO).
        </p>
      </Section>

      <Section title="3. Jakie dane przetwarzamy">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Dane konta</strong> — adres e-mail, hasło (przechowywane w
            postaci zaszyfrowanego skrótu, nigdy w postaci jawnej), preferowany
            język interfejsu.
          </li>
          <li>
            <strong>Dane profilu</strong> — imię, nazwisko, miasto, kraj, klub,
            ranking FIDE, ranking szachowy/backgammonowy, tytuł BMAB (w
            przypadku backgammona), krótka notka o sobie, zdjęcie profilowe.
            Większość pól jest opcjonalna.
          </li>
          <li>
            <strong>Dane logowania przez Google</strong> — jeżeli korzystasz z
            opcji „Zaloguj się przez Google", otrzymujemy od Google Twój adres
            e-mail, imię i nazwisko oraz publiczny obrazek profilu. Nie
            otrzymujemy dostępu do Twojej skrzynki Gmail ani innych usług
            Google.
          </li>
          <li>
            <strong>Dane turniejowe</strong> — zapisy do turniejów, parowania,
            wyniki spotkań, klasyfikacje. Te dane są publiczne w ramach
            turnieju.
          </li>
          <li>
            <strong>Powiadomienia push</strong> — jeżeli włączysz powiadomienia
            w przeglądarce, przechowujemy identyfikator subskrypcji niezbędny
            do wysyłania Ci powiadomień.
          </li>
          <li>
            <strong>Dane techniczne</strong> — adres IP, typ przeglądarki,
            informacja o urządzeniu, logi serwera (przechowywane przez okres
            niezbędny do zapewnienia bezpieczeństwa i diagnostyki).
          </li>
        </ul>
      </Section>

      <Section title="4. Cele i podstawy prawne przetwarzania">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Świadczenie usługi</strong> (utworzenie konta, udział w
            turniejach, prezentacja wyników) — art. 6 ust. 1 lit. b RODO
            (wykonanie umowy).
          </li>
          <li>
            <strong>Komunikacja transakcyjna</strong> (potwierdzenie
            rejestracji, reset hasła, zaproszenia do turniejów) — art. 6 ust.
            1 lit. b RODO.
          </li>
          <li>
            <strong>Powiadomienia push</strong> — art. 6 ust. 1 lit. a RODO
            (zgoda, którą wyrażasz wyraźnie włączając powiadomienia w
            przeglądarce; możesz ją w każdej chwili wycofać w ustawieniach).
          </li>
          <li>
            <strong>Zapewnienie bezpieczeństwa systemu, zapobieganie
            nadużyciom</strong> — art. 6 ust. 1 lit. f RODO (uzasadniony
            interes Administratora).
          </li>
          <li>
            <strong>Prowadzenie historii turniejów i statystyk graczy</strong>{' '}
            — art. 6 ust. 1 lit. f RODO (uzasadniony interes Administratora i
            uczestników w zachowaniu rekordów rozgrywek).
          </li>
        </ul>
      </Section>

      <Section title="5. Odbiorcy danych">
        <p>
          Twoje dane mogą być przekazywane następującym podmiotom
          przetwarzającym, świadczącym usługi techniczne na rzecz
          Administratora:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6">
          <li>
            <strong>Supabase</strong> (Supabase Inc., USA) — usługa baz danych
            i uwierzytelniania. Dane przechowywane są w regionie europejskim
            (Frankfurt).
          </li>
          <li>
            <strong>Fly.io</strong> (Fly.io Inc., USA) — hosting aplikacji
            backendowej. Maszyny zlokalizowane są w regionie europejskim
            (Frankfurt).
          </li>
          <li>
            <strong>Vercel</strong> (Vercel Inc., USA) — hosting interfejsu
            użytkownika.
          </li>
          <li>
            <strong>Resend</strong> (Resend Inc., USA) — wysyłka wiadomości
            transakcyjnych (potwierdzenia konta, reset hasła, zaproszenia).
          </li>
          <li>
            <strong>Google LLC</strong> — tylko jeżeli logujesz się przez
            Google.
          </li>
          <li>
            <strong>Mozilla, Apple, Google</strong> — dostawcy usług push
            notifications w przeglądarkach (tylko jeżeli włączysz
            powiadomienia).
          </li>
        </ul>
        <p className="mt-3">
          Część z wyżej wymienionych podmiotów ma siedzibę w USA. Transfer
          danych poza Europejski Obszar Gospodarczy odbywa się na podstawie
          standardowych klauzul umownych zatwierdzonych przez Komisję
          Europejską (art. 46 RODO).
        </p>
      </Section>

      <Section title="6. Okres przechowywania">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            Dane konta — przez okres, w którym konto pozostaje aktywne, oraz
            do momentu usunięcia konta na Twój wniosek.
          </li>
          <li>
            Dane turniejowe (parowania, wyniki) — bezterminowo jako historia
            rozgrywek, chyba że zażądasz usunięcia danych identyfikujących Cię
            w tych zapisach.
          </li>
          <li>
            Logi serwera — do 30 dni od ich utworzenia.
          </li>
          <li>
            Tokeny sesji i subskrypcje push — usuwane wraz z wylogowaniem lub
            wyłączeniem powiadomień.
          </li>
        </ul>
      </Section>

      <Section title="7. Twoje prawa">
        <p>Na podstawie RODO przysługują Ci następujące prawa:</p>
        <ul className="mt-2 list-disc space-y-1 pl-6">
          <li>prawo dostępu do swoich danych (art. 15 RODO);</li>
          <li>prawo do sprostowania danych (art. 16 RODO);</li>
          <li>prawo do usunięcia danych — „prawo do bycia zapomnianym" (art. 17 RODO);</li>
          <li>prawo do ograniczenia przetwarzania (art. 18 RODO);</li>
          <li>prawo do przenoszenia danych (art. 20 RODO);</li>
          <li>prawo do sprzeciwu wobec przetwarzania (art. 21 RODO);</li>
          <li>prawo do wycofania zgody w dowolnym momencie (art. 7 ust. 3 RODO);</li>
          <li>
            prawo do wniesienia skargi do Prezesa Urzędu Ochrony Danych
            Osobowych (ul. Stawki 2, 00-193 Warszawa).
          </li>
        </ul>
        <p className="mt-3">
          W celu skorzystania ze swoich praw skontaktuj się z nami pod
          adresem{' '}
          <a
            className="text-primary underline"
            href="mailto:admin@backgammon.pl"
          >
            admin@backgammon.pl
          </a>
          . Odpowiemy bez zbędnej zwłoki, nie później niż w terminie miesiąca
          od otrzymania żądania.
        </p>
      </Section>

      <Section title="8. Pliki cookies i pamięć lokalna przeglądarki">
        <p>
          Korzystamy z pamięci lokalnej przeglądarki (localStorage) wyłącznie w
          celach niezbędnych do działania usługi:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6">
          <li>token sesji uwierzytelniania (po zalogowaniu);</li>
          <li>zapamiętanie wybranego języka interfejsu.</li>
        </ul>
        <p className="mt-3">
          Nie używamy plików cookies do śledzenia, profilowania ani celów
          marketingowych. Nie korzystamy z zewnętrznych narzędzi analitycznych
          (Google Analytics itp.).
        </p>
      </Section>

      <Section title="9. Bezpieczeństwo">
        <p>
          Stosujemy techniczne i organizacyjne środki bezpieczeństwa
          adekwatne do ryzyka, w tym: szyfrowanie połączeń (HTTPS), szyfrowane
          przechowywanie haseł, kontrolę dostępu do bazy danych na poziomie
          wierszy (Row Level Security), regularne aktualizacje oprogramowania,
          monitorowanie luk bezpieczeństwa w bibliotekach zależnych.
        </p>
      </Section>

      <Section title="10. Zmiany polityki prywatności">
        <p>
          Możemy aktualizować niniejszą politykę. O istotnych zmianach
          poinformujemy Cię e-mailem lub w aplikacji co najmniej 7 dni przed
          ich wejściem w życie. Data ostatniej aktualizacji widoczna jest na
          początku dokumentu.
        </p>
      </Section>

      <hr className="my-8 border-border" />

      <Section title="English summary">
        <p className="text-sm text-muted-foreground">
          This privacy policy (Polish version above is authoritative) covers
          the personal data we process when you use the tournament platform:
          email, profile fields you enter, OAuth profile (if you sign in via
          Google), tournament participation records, optional push subscription
          identifiers, and server logs.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Data is stored on Supabase (EU/Frankfurt), Fly.io (EU/Frankfurt),
          Vercel (global edge for static assets), and routed via Resend for
          transactional email. We don't use any analytics or tracking
          products. You have full GDPR rights (access, rectification, erasure,
          portability, restriction, objection, withdrawal of consent) — contact
          the address at the top to exercise them.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xl font-semibold">{title}</h2>
      <div className="space-y-3 text-foreground/90">{children}</div>
    </section>
  );
}
